import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const emptyForm = {
  name: '',
  address_line: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
  hours_text: '',
  is_active: true
};

const AdminOutlets = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const fetchOutlets = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken(true);
      if (!token) {
        navigate('/dashboard/login');
        return;
      }

      // all=1 so inactive outlets are still visible here for re-activation, unlike the
      // public checkout picker which only ever sees active ones.
      const response = await fetch(`${API_BASE}/api/outlets?all=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch outlets');
      const data = await response.json();

      if (data.success) {
        setOutlets(Array.isArray(data.data) ? data.data : []);
      } else {
        throw new Error(data.message || 'Failed to load outlets');
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
      toast.error(error.message || 'Failed to load outlets');
      setOutlets([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getToken, navigate]);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'employee')) {
      fetchOutlets();
    } else if (user) {
      navigate('/dashboard');
    }
  }, [fetchOutlets, navigate, user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingOutlet(null);
    setShowForm(false);
  };

  const handleEdit = (outlet) => {
    setEditingOutlet(outlet);
    setFormData({
      name: outlet.name || '',
      address_line: outlet.address_line || '',
      city: outlet.city || '',
      state: outlet.state || '',
      zip_code: outlet.zip_code || '',
      phone: outlet.phone || '',
      hours_text: outlet.hours_text || '',
      is_active: !!outlet.is_active
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = getToken(true);
      if (!token) {
        navigate('/dashboard/login');
        return;
      }

      const url = editingOutlet
        ? `${API_BASE}/api/outlets/${editingOutlet.id}`
        : `${API_BASE}/api/outlets`;
      const method = editingOutlet ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingOutlet ? 'Outlet updated' : 'Outlet added');
        resetForm();
        fetchOutlets();
      } else {
        throw new Error(data.message || 'Failed to save outlet');
      }
    } catch (error) {
      console.error('Error saving outlet:', error);
      toast.error(error.message || 'Failed to save outlet');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (outlet) => {
    if (!window.confirm(`Delete "${outlet.name}"? Past orders that were picked up here will keep their history, just without a linked outlet.`)) {
      return;
    }

    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/outlets/${outlet.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Outlet deleted');
        fetchOutlets();
      } else {
        throw new Error(data.message || 'Failed to delete outlet');
      }
    } catch (error) {
      console.error('Error deleting outlet:', error);
      toast.error(error.message || 'Failed to delete outlet');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading outlets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">Store Outlets</h1>
          <p className="text-gray-600">
            Locations customers can choose for "Collect from Outlet" at checkout.
            Turn the feature on under Settings → Payment &amp; Delivery Options.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-indigo-700 lg:w-auto"
        >
          <PlusIcon className="w-5 h-5" />
          Add Outlet
        </button>
      </div>

      {/* Outlets List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
        {outlets.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">
            No outlets yet. Click "Add Outlet" to add your first pickup location.
          </div>
        ) : (
          outlets.map((outlet) => (
            <div key={outlet.id} className="p-4 sm:p-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <MapPinIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold text-gray-900">{outlet.name}</h2>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        outlet.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {outlet.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {outlet.address_line}, {outlet.city}, {outlet.state} {outlet.zip_code}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {outlet.phone && <span>Phone: {outlet.phone}</span>}
                    {outlet.phone && outlet.hours_text && <span> · </span>}
                    {outlet.hours_text && <span>{outlet.hours_text}</span>}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 sm:flex-shrink-0">
                <button
                  onClick={() => handleEdit(outlet)}
                  className="flex items-center justify-center gap-2 rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(outlet)}
                  className="flex items-center justify-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingOutlet ? 'Edit Outlet' : 'Add New Outlet'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Outlet Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Viverra Fancy - Ranni"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address Line *</label>
                <input
                  type="text"
                  name="address_line"
                  value={formData.address_line}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Zip Code *</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="+919946978353"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Opening Hours</label>
                  <input
                    type="text"
                    name="hours_text"
                    value={formData.hours_text}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Mon-Sat, 10am - 8pm"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4"
                />
                <span className="text-sm font-semibold text-gray-700">
                  Active (visible to customers as a pickup location at checkout)
                </span>
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-6 py-2 rounded-lg text-white transition-colors font-semibold ${
                    saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {saving ? 'Saving…' : editingOutlet ? 'Update Outlet' : 'Create Outlet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOutlets;
